/**
 * <Spinner> — branded loading spinner.
 *
 * Per locked decision D.1: SMMS uses a spinner with the brand "S" mark.
 * The spinner draws an arc segment around the SMMS-S to communicate
 * "we're working on it" without the busy circle-of-dashes pattern that
 * looks like every other generic SaaS app.
 *
 * Sizes match Icon: xs (16) / sm (20) / md (24) / lg (32). Used in:
 *   - Button loading states (Button overlays a small spinner)
 *   - Page-level loading (use size='lg' centered)
 *   - DataTable loading (size='md' inside the empty body cell)
 *
 * Spec: docs/design-system/08-modals-drawers-feedback.md.
 */

import { type SVGAttributes } from 'react';
import { cn } from '../../utils/cn';

export type SpinnerSize = 'xs' | 'sm' | 'md' | 'lg';

export interface SpinnerProps extends Omit<SVGAttributes<SVGSVGElement>, 'children'> {
  size?: SpinnerSize;
  /** Accessible label. Default 'Loading'. */
  label?: string;
}

const SIZE_PIXELS: Record<SpinnerSize, number> = {
  xs: 16,
  sm: 20,
  md: 24,
  lg: 32,
};

const SIZE_CLASSES: Record<SpinnerSize, string> = {
  xs: 'w-4 h-4',
  sm: 'w-5 h-5',
  md: 'w-6 h-6',
  lg: 'w-8 h-8',
};

export function Spinner({ size = 'md', label = 'Loading', className, ...rest }: SpinnerProps) {
  const px = SIZE_PIXELS[size];
  return (
    <svg
      width={px}
      height={px}
      viewBox="0 0 32 32"
      fill="none"
      role="status"
      aria-label={label}
      className={cn(SIZE_CLASSES[size], 'animate-spin shrink-0 text-primary', className)}
      {...rest}
    >
      {/* Faint full ring */}
      <circle cx="16" cy="16" r="13" stroke="currentColor" strokeOpacity="0.18" strokeWidth="2.5" />
      {/* Active arc segment — drives the rotation feel */}
      <path
        d="M16 3 a13 13 0 0 1 13 13"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      {/* SMMS "S" mark — counter-rotates so it stays upright. */}
      <text
        x="16"
        y="20"
        textAnchor="middle"
        fontFamily="Inter, system-ui, sans-serif"
        fontSize="13"
        fontWeight="700"
        fill="currentColor"
        style={{ transformOrigin: '16px 16px', transform: 'rotate(0deg)' }}
        // Rotate the text in opposite direction of the parent <svg> animation
        // so the letter "S" stays upright while the ring rotates around it.
        // Animation is added via inline keyframes so it doesn't depend on
        // tailwind-config additions and works at any DPR.
        className="[animation:smms-counter-spin_1s_linear_infinite]"
      >
        S
      </text>
      <style>{`
        @keyframes smms-counter-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(-360deg); }
        }
      `}</style>
    </svg>
  );
}

Spinner.displayName = 'Spinner';

export default Spinner;
